import type { NdtIndicationRow } from '../components/dashboard/drilldown/drillDownTypes';
import type { PassViewRecord } from './fetchWeldViewApis';
import type { WpsViolationRecord } from './wpsViolationModel';

export const WELD_DETAIL_STALE_MS = 5 * 60 * 1000;

export interface WeldDetailCacheEntry {
  passes: PassViewRecord[];
  violations: WpsViolationRecord[];
  welderIds: string;
  ndtLogs?: NdtIndicationRow[];
  loadedAt: number;
  wpsError?: string | null;
}

const cache = new Map<string, WeldDetailCacheEntry>();

export function weldDetailCacheKey(sessionKey: string, rowKey: string): string {
  return `${sessionKey}::${rowKey}`;
}

export function getCachedWeldDetail(key: string): WeldDetailCacheEntry | null {
  return cache.get(key) ?? null;
}

export function setCachedWeldDetail(key: string, entry: WeldDetailCacheEntry): void {
  cache.set(key, entry);
}

export function patchCachedWeldDetail(
  key: string,
  patch: Partial<WeldDetailCacheEntry>,
): WeldDetailCacheEntry | null {
  const prev = cache.get(key);
  if (!prev) return null;
  const next = { ...prev, ...patch, loadedAt: patch.loadedAt ?? Date.now() };
  cache.set(key, next);
  return next;
}

export function isWeldDetailCacheFresh(
  entry: WeldDetailCacheEntry | null | undefined,
  staleMs = WELD_DETAIL_STALE_MS,
): boolean {
  if (!entry) return false;
  return Date.now() - entry.loadedAt < staleMs;
}

/** Drop entries for a project/pipe session (pipe or project change). */
export function clearWeldDetailCacheForSession(sessionKey: string): void {
  const prefix = `${sessionKey}::`;
  Array.from(cache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  });
}

/** Test helper — clears the entire in-memory cache. */
export function clearAllWeldDetailCache(): void {
  cache.clear();
}
